const _ = {
  map: require('lodash/map'),
  groupBy: require('lodash/groupBy'),
  each: require('lodash/each'),
  isNil: require('lodash/isNil'),
  isEmpty: require('lodash/isEmpty'),
  startCase: require('lodash/startCase'),
  toLower: require('lodash/toLower'),
}

const COLON = ':'
const LINE_BREAK = '<br />'
const DOUBLE_LINE_BREAK = '<br /><br />'
const HYPERLINK_BASE_TEXT = 'Link '
const DOUBLE_SPACE = '  '

// TODO - Consider moving this into the API side
function formattedDescriptionFromResponse(blip) {
  const iconRow = constructIconRowHTML(blip)
  const labels = getLabelsAsPillsHTML(blip)
  const description = constructDescriptionHTML(blip)
  const sponsor = blip.sponsor
  const champions = blip.champions
  const championNames = blip.champions ? champions.map((champion) => champion.name).join(', ') : null
  const stewards = blip.stewards
  const stewardNames = blip.stewards ? stewards.map((steward) => steward.name).join(', ') : null
  const movementTimeline = constructMovementTimelineHTML(blip)
  const readingMaterial = constructReadingMaterialsHTML(blip)
  const comments = constructCommentsHTML(blip)
  const relatedBlips = constructRelatedBlipsHTML(blip)

  let formattedDescription = addIfNotEmpty(null, iconRow + labels, LINE_BREAK)
  formattedDescription += addIfNotEmpty('Description', description)

  // Legacy support - only attempt to display Sponsors when Stewards wasn't available
  if (!stewardNames) {
    formattedDescription += addIfNotEmpty('Sponsor', sponsor)
  }

  formattedDescription += addIfNotEmpty('Steward(s)', stewardNames)
  formattedDescription += addIfNotEmpty('Champion(s)', championNames)
  formattedDescription += addIfNotEmpty('Timeline', movementTimeline)
  formattedDescription += addIfNotEmpty('Reading Materials', readingMaterial)
  formattedDescription += addIfNotEmpty('Related Blips', relatedBlips)
  formattedDescription += addIfNotEmpty('Comments', comments)

  return formattedDescription
}

function addIfNotEmpty(title, value, line_break = DOUBLE_LINE_BREAK) {
  let formattedHTML = ''

  if (_.isNil(value) || _.isEmpty(value)) {
    return ''
  }

  if (title) {
    formattedHTML += boldFormat(title + COLON) + LINE_BREAK
  }

  formattedHTML += value + line_break

  return formattedHTML
}

function constructDescriptionHTML(blip) {
  let descriptionHTML = blip.description

  if (descriptionHTML) {
    // Headings h1. ... h6.
    descriptionHTML = descriptionHTML.replace(/^h([1-6])\.\s*(.*)$/gm, '<h$1>$2</h$1>');

    // Convert Atlassian-style bullets (* ...)
    descriptionHTML = descriptionHTML.replace(
      /(?:^|\n)\* (.*?)(?=\n|$)/g,
      (match, content) => `<li>${content}</li>`
    );
    // Wrap <li> items in a <ul>
    descriptionHTML = descriptionHTML.replace(/(<li>[\s\S]*?<\/li>)/gms, '<ul>$1</ul>');

    // Bold
    descriptionHTML = descriptionHTML.replace(/\*(.*?)\*/g, '<strong>$1</strong>');

    // Italics
    descriptionHTML = descriptionHTML.replace(/_(.*?)_/g, '<em>$1</em>');

    // Underline italics
    descriptionHTML = descriptionHTML.replace(/\{\{(.*?)\}\}/g, '<em><u>$1</u></em>');

    // Colon fix inside bold text
    descriptionHTML = descriptionHTML.replace(/<\/strong>:/g, '</strong>:');

    // Atlassian link [text|url]
    descriptionHTML = descriptionHTML.replace(/\[(.*?)\|(.*?)\]/g, '<a href="$2">$1</a>');

    // Trim double <ul> if wrapping added multiple times
    descriptionHTML = descriptionHTML.replace(/<\/ul>\s*<ul>/g, '');
  }

  return descriptionHTML
}

function constructReadingMaterialsHTML(blip) {
  let formattedReadingMaterials = ''

  const readingMaterials = blip.readingMaterials ? blip.readingMaterials.split('\n') : []

  readingMaterials.forEach((link) => {
    if (link) {
      link = link
        .replace('[', '')
        .replace(']', '')
        .replace('*', '')
        .replace("# ", "")
        .replace('|smart-link', '').trim()

      const url = link.substr(link.indexOf('|') + 1)
      const textToDisplay = getFriendlyHyperLinkDisplayText(link)
      const listItem = '<li><a href="' + url + '">' + textToDisplay + '</a></li>'
      formattedReadingMaterials += listItem
    }
  })
  return formattedReadingMaterials ? '<ul class="wkc-bullet-with-indent">' + formattedReadingMaterials + '</ul>' : ''
}

function constructIconRowHTML(blip) {
  let iconHTML =
    '<a class="jira-icon wkc-icon" href="' + blip.link + '"  target="_blank" aria-label="' + blip.id + '"></a>'

  if (blip.slackChannel) {
    let slackUrl = blip.slackChannel;

    if (!blip.slackChannel.startsWith('https://')) {
      slackUrl = 'https://wfs-coo.slack.com/archives/' + blip.slackChannel.replace('#', '')
    }


    iconHTML +=
      '<a class="slack-icon wkc-icon" href="' +
      slackUrl +
      '" target="_blank" aria-label=Slack Channel"></a>'
  }

  if (blip.thoughtworksBlipArchive) {
    iconHTML +=
      '<a class="tech-radar-icon wkc-icon" href="' +
      blip.thoughtworksBlipArchive +
      '"  target="_blank" aria-label="Thoughtworks Blip Archive"></a>'
  }

  if (blip.githubRepo) {
    const repoName = blip.githubRepo.replace('https://github.com/', '')
    iconHTML +=
      '<a class="github-icon wkc-icon" href="' +
      blip.githubRepo +
      '" target="_blank" aria-label="' +
      repoName +
      '"></a>'
  }

  const factSheets = blip.factSheets ?? []

  factSheets.forEach(function (factSheet, i) {
    iconHTML +=
      '<a class="leanix-icon wkc-icon" href="' +
      factSheet.link +
      '" target="_blank" aria-label="Fact Sheet ' +
      (i + 1) +
      '"></a>'
  })

  return iconHTML
}

function constructRelatedBlipsHTML(blip) {
  let formattedRelatedBlips = ''

  if (blip.relatedBlips) {
    blip.relatedBlips.forEach((relatedBlip) => {
      // FIXME - this should never be null
      if (relatedBlip) {
        const listItem =
          '<li><a class="jira-icon wkc-icon" href="' +
          relatedBlip.link +
          '">' +
          relatedBlip.id +
          DOUBLE_SPACE +
          relatedBlip.name +
          '</a></li>'
        formattedRelatedBlips += listItem
      } else {
        console.error(`ERROR: A related blip for ${blip.id} ${blip.name} was null`)
      }
    })
  }

  return formattedRelatedBlips ? '<ul class="wkc-bullet-with-indent">' + formattedRelatedBlips + '</ul>' : ''
}

function constructMovementTimelineHTML(blip) {
  let formattedMovementTimeline = ''

  const activeTimelineEvents = blip.history.filter((h) => h.movement != null && h.movement != "NO_CHANGE" && h.publicationName != "latest");
  activeTimelineEvents.reverse();

  activeTimelineEvents.forEach((event) => {
    let listItem =
      '<li><a href="/?publicationName=' + encodeURIComponent(event.publicationName) + '">' +
      event.publicationName +
      '</a> : '

    switch (event.movement) {
      case 'NEW':
        listItem += 'New blip appeared as '
        break;
      case 'MOVED_IN':
        listItem += 'Blip moved in to '
        break;
      case 'MOVED_OUT':
        listItem += 'Blip moved out to '
        break;
    }

    listItem += event.status + '</li>'
    formattedMovementTimeline += listItem
  })

  return formattedMovementTimeline ? '<ul class="wkc-bullet-with-indent">' + formattedMovementTimeline + '</ul>' : ''
}


function constructCommentsHTML(blip) {
  let formattedComments = ''

  if (blip.comments) {
    blip.comments.forEach((comment) => {
      const listItem = '[' + new Date(comment.updated).toDateString() + '] ' + comment.author.name + ':' + DOUBLE_SPACE + '<em>' + comment.body + '</em>'
      formattedComments += '<li>' + listItem + '</li>';
    })
  }

  return formattedComments ? '<ul class="wkc-bullet-with-indent">' + formattedComments + '</ul>' : ''
}

function getLabelsAsPillsHTML(blip) {
  let formattedLabels = ''

  let urlParams = new URLSearchParams(window.location.search)

  if (blip.labels) {
    blip.labels.forEach((label) => {
      if (displayPillByLabel(label)) {
        urlParams.set('label', label)
        const labelPill = '<a class="wkc-pill" href="?' + urlParams.toString() + '">' + label + '</a>' + DOUBLE_SPACE
        formattedLabels += labelPill
      }
    })
  }

  return formattedLabels
}

function displayPillByLabel(label) {
  return !(
    label == null ||
    label.match(/^\s*$/) !== null ||
    // Skip any label that contains numbers, as they're likely related to Quarters or Sprints
    /\d/.test(label)
  )
}

function getFriendlyHyperLinkDisplayText(readingMaterial) {
  if (readingMaterial === '') {
    return ''
  }
  try {
    const providedFriendlyText = readingMaterial.substr(0, readingMaterial.indexOf('|')).trim()

    if (providedFriendlyText && providedFriendlyText.substr(0, 4) != 'http') {
      return `Web: ${providedFriendlyText}`
    }

    var linkSource = ''
    var text = HYPERLINK_BASE_TEXT

    const url = readingMaterial.substr(readingMaterial.indexOf('|') + 1)
    const urlObj = new URL(url)
    const hostname = urlObj.hostname

    if (hostname.includes('wfscorp.atlassian.net')) {
      linkSource = 'Confluence'
      text = url
        .substr(url.lastIndexOf('/') + 1) // Title of wiki begins from the last slash
        .replace(/\+/g, ' ') // Replacing + with spaces
        .trim()

      if (text.includes('?')) {
        text = text.substr(0, text.indexOf('?')) // Strip off any query parameter parts
      }
    } else {
      linkSource = 'Web'

      const humanReadableDomain = getHumanReadableDomain(hostname)
      const domains = humanReadableDomain.split('.')
      text = titleCase(domains[domains.length - 1])
    }

    return `${linkSource}: ${titleCase(text)}`
  } catch (err) {
    console.error('Error parsing link: ' + readingMaterial)
    return readingMaterial
  }
}

/**
 * Given a url string, returns the domain without www and Top-Level Domain
 * E.g.
 * https://www.google.com --> google
 * https://en.wikipedia.com --> en.wikipedia
 * Ref: https://dev.to/thepracticaldev/daily-challenge-89-extract-domain-name-from-url-1ajb
 */
function getHumanReadableDomain(url) {
  return /^(?:(?:www\.)?(.+?)(?:\.[a-zA-Z]+)?)?$/.exec(url || '')[1]
}

function titleCase(text) {
  return _.startCase(_.toLower(text))
}

function boldFormat(text) {
  return '<b>' + text + '</b>'
}

const HEADING_COLORS = ['rgb(225,106,124)', 'rgb(204,133,10)', 'rgb(71,161,173)', 'rgb(107,158,120)']
const ROW_COLORS = ['rgb(225,106,124,0.5)', 'rgb(204,133,10,0.5)', 'rgb(71,161,173,0.5)', 'rgb(107,158,120,0.5)']

function getTableHeadingRow(data) {
  let colorIndex = 0
  let headingRowHTML = '<tr>'

  _.map(data, function (value, key) {
    headingRowHTML += `<th class="table-blips-heading" style="background-color: ${HEADING_COLORS[colorIndex]};"><h4>${key}</h4></th>`
    colorIndex += 1
  })

  headingRowHTML += '</tr>'

  return headingRowHTML
}

function getCellColor(colNumber) {
  return ROW_COLORS[colNumber]
}

function prettifyMovementName(movement) {
  switch (movement) {
    case 'NEW':
      return '"First appearance"'
    case 'NO_CHANGE':
      return 'No change'
    case 'UNPUBLISHED':
      return 'Unpublished'
    case 'MOVED_IN':
      return '"Moved in"'
    case 'MOVED_OUT':
      return '"Moved out"'
    case 'OFF_RADAR':
      return '"Off Radar"'
    case 'CORE':
      return '"Core stack"'
    default:
      return 'Unknown'
  }
}

function findLastPublishedRecord(blip) {
  let lastSeen
  _.each(blip.history, function (history) {
    if (history.status && history.status !== 'OFF_RADAR' && history.status !== 'CORE') {
      lastSeen = history
    }
  })
  return lastSeen
}

module.exports = {
  formattedDescriptionFromResponse,
  titleCase,
  getCellColor,
  getTableHeadingRow,
  prettifyMovementName,
  findLastPublishedRecord,
}
